import { isEqual } from 'lodash';
import isEmpty from 'lodash/isEmpty';

export const invalidValues = ['', null, undefined];

export const filterInvalidValuesFormValues = (formValues) => {
  let result = {};

  for (const [key, value] of Object.entries(formValues)) {
    if (Array.isArray(value)) {
      if (
        value.length === 0 ||
        value.some((item) => invalidValues.includes(item))
      ) {
        result = {
          ...result,
          [key]: value.filter((item) => !invalidValues.includes(item)),
        };
      } else {
        result = {
          ...result,
          [key]: value,
        };
      }
    } else {
      result = {
        ...result,
        [key]: value,
      };
    }
  }

  return result;
};

export const checkPassedRequirementProduct = (product) => {
  if (isEmpty(product?.activeResult) || product?.isSuccess) return true;

  return Boolean(product?.activeResult?.isSuccess);
};

const getFullPathDisplayName = ({ parentDisplayFullName, displayName }) => {
  const fullPathDisplayName = `${parentDisplayFullName}${
    parentDisplayFullName && displayName ? ' - ' : ''
  }${displayName}`;

  return fullPathDisplayName;
};

const flattenField = ({
  properties,
  mappingFieldFn = () => {},
  mappingModuleFn = () => {},
  moduleField,
  parentDisplayFullName = '',
}) => {
  let result = {};

  for (const field of properties) {
    const { fieldFullPath, properties: childProperties } = field;

    const fullPathDisplayName = getFullPathDisplayName({
      parentDisplayFullName,
      displayName: field?.displayName,
    });

    result = {
      ...result,
      [fieldFullPath]: {
        ...field,
        fullPathDisplayName,
        ...mappingFieldFn(field),
        ...mappingModuleFn(moduleField),
      },
      ...flattenField({
        properties: childProperties,
        mappingFieldFn,
        mappingModuleFn,
        moduleField,
        parentDisplayFullName: fullPathDisplayName,
      }),
    };
  }

  return result;
};

const defaultMappingModuleFn = (moduleField) => ({
  moduleDisplayName: moduleField.displayName,
  moduleName: moduleField.moduleName,
});

const defaultMappingFieldFn = () => ({});

export const flattenRequiredProductFields = ({
  allFields = [],
  mappingFieldFn = defaultMappingFieldFn,
  mappingModuleFn = defaultMappingModuleFn,
}) => {
  let result = {};

  for (const moduleField of allFields) {
    result = {
      ...result,
      ...flattenField({
        properties: moduleField.properties,
        mappingFieldFn,
        mappingModuleFn,
        moduleField,
        parentDisplayFullName: '',
      }),
    };
  }

  return result;
};

export const getPassedProducts = (
  validateProducts,
  initialValidateProducts
) => {
  let passedProductIds = [];

  if (!validateProducts || !validateProducts?.length) return [];

  // Check if all products have null activeResult (meaning no validation was performed)
  const hasNoValidation = validateProducts.every(
    (product) => product?.activeResult === null
  );

  if (hasNoValidation) {
    // If no validation results, return all initial products
    passedProductIds = initialValidateProducts.map(
      (product) => product?.productId
    );
  } else {
    // Normal validation logic
    passedProductIds = validateProducts
      .filter((product) => checkPassedRequirementProduct(product))
      .map((product) => product?.activeResult?.productId);
  }

  // get meta data for product (fee, packageLevel)
  const passedProducts = initialValidateProducts
    .filter((product) => passedProductIds.includes(product?.productId))
    .map((product) => {
      // Find the corresponding product in validateProducts to get updated packageLevel
      const validatedProduct = validateProducts.find(
        (vp) => vp?.activeResult?.productId === product?.productId
      );

      return {
        ...product,
        packageLevel:
          validatedProduct?.activeResult?.packageLevel ?? product?.packageLevel,
      };
    });

  return passedProducts;
};

export const getDifferentFields = (objA, objB) => {
  const diff = {};

  Object.keys(objA).forEach((key) => {
    const a = invalidValues.includes(objA[key]) ? null : objA[key];
    const b = invalidValues.includes(objA[key]) ? null : objB[key];

    if (!isEqual(a, b)) {
      diff[key] = { from: objA[key], to: objB[key] };
    }
  });

  return diff;
};

export const filteredColumnsByFee = ({ canBypassCharge, columns }) => {
  const filteredColumns = canBypassCharge
    ? columns.filter(
        (col) => col?.field !== 'statusCharge' && col?.field !== 'fee'
      )
    : columns;

  return filteredColumns;
};

export const calculateTotalFee = (productList = []) => {
  const totalFee = productList.reduce((accumulator, product) => {
    return accumulator + product.fee;
  }, 0);
  return totalFee;
};
